// frontend/src/services/api.js

import axios from 'axios';

// ------------------------------------------------------------------
// 🎯 پیکربندی پایه
// ------------------------------------------------------------------

const DEFAULT_BACKEND_URL = 'https://farzaneh-backend-django.onrender.com';

const API_HOST = process.env.REACT_APP_API_BASE_URL || DEFAULT_BACKEND_URL;

// 🟢 تضمین عدم وجود اسلش در انتهای API_BASE_URL
// برای جلوگیری از Double-Slashing، ابتدا API_HOST را نرمالایز می‌کنیم
const API_HOST_CLEAN = API_HOST.replace(/\/+$/, '');

// اگر فرض می‌کنیم API_HOST فقط دامنه (Host) است و نه مسیر کامل:
const API_BASE_URL = `${API_HOST_CLEAN}/api/v1`.replace(/\/+$/, ''); 

// 💡 ماژول برای اتصال به بک‌اند
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept-Language': 'fa',
    },
    withCredentials: true, 
});

// ------------------------------------------------------------------
// 🎯 Interceptor برای مدیریت توکن و CSRF (اصلاح شده برای 401 عمومی)
// ------------------------------------------------------------------
api.interceptors.request.use(
    (config) => {
        
        // 🛑 ۱. اصلاح کلیدی: پاکسازی هدر Authorization در ابتدا
        // این گام از ارسال توکن‌های نامعتبر یا قدیمی برای درخواست‌های عمومی جلوگیری می‌کند.
        // اگر requiresAuth وجود ندارد یا false است، هدر پاک می‌شود.
        if (!config.requiresAuth) {
            delete config.headers.Authorization;
        }

        // ۲. JWT Authentication
        if (config.requiresAuth === true) {
            const accessToken = localStorage.getItem('access_token');
            if (accessToken) { 
                config.headers.Authorization = `Bearer ${accessToken}`;
            } else {
                 // اگر requiresAuth درست است اما توکنی در LocalStorage نیست، مطمئن شویم
                 // که Authorization هدر ندارد تا درخواست با شکست احراز هویت به سرور برسد 
                 // و Interceptor پاسخ بتواند 401 را مدیریت کند.
                 delete config.headers.Authorization;
            }
        }
        
        // ۳. CSRF Token Management (بدون تغییر)
        const isSafeMethod = ['GET', 'HEAD', 'OPTIONS'].includes(config.method.toUpperCase());
        // یافتن CSRFToken از کوکی
        const csrfToken = document.cookie.split('; ').find(row => row.startsWith('csrftoken='))?.split('=')[1];
        
        if (csrfToken && !isSafeMethod) { 
           config.headers['X-CSRFToken'] = csrfToken;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// ------------------------------------------------------------------
// 🚀 Interceptor برای مدیریت رفرش توکن (401 Error Handling)
// ------------------------------------------------------------------
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // بررسی خطای 401 که نیاز به رفرش توکن دارد
        if (error.response?.status === 401 && error.response?.data?.code === 'token_not_valid' && !originalRequest._retry) {
            
            originalRequest._retry = true; 
            const refreshToken = localStorage.getItem('refresh_token');

            if (!refreshToken) {
                // توکن رفرش وجود ندارد: خروج
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                window.location.href = '/login'; 
                return Promise.reject(error);
            }

            try {
                // تلاش برای دریافت توکن جدید
                const response = await axios.post(`${API_BASE_URL}/token/refresh/`, { 
                    refresh: refreshToken
                });

                const newAccessToken = response.data.access;
                localStorage.setItem('access_token', newAccessToken);

                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                
                // 💡 در اینجا باید 'requiresAuth' را دوباره تنظیم کنید اگر در فرانت اند استفاده می کنید
                if (originalRequest.requiresAuth === undefined) {
                   originalRequest.requiresAuth = true;
                }

                // تکرار درخواست اصلی با توکن جدید
                return api(originalRequest);

            } catch (refreshError) {
                // اگر رفرش کردن شکست خورد: خروج
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                window.location.href = '/login'; 
                return Promise.reject(refreshError);
            }
        }
        
        // 💡 مدیریت 401 عمومی (که token_not_valid نیست)
        // اگر 401 آمد، تمام توکن‌ها را حذف می‌کنیم تا سیستم احراز هویت ریست شود
        if (error.response?.status === 401) {
             localStorage.removeItem('access_token');
             localStorage.removeItem('refresh_token');
             // اگر برای یک مسیر احراز هویت شده بود (مانند Wallet)، کاربر را به صفحه ورود هدایت می‌کنیم
             if (originalRequest.requiresAuth) {
                 window.location.href = '/login'; 
             }
        }
        
        return Promise.reject(error);
    }
);

export default api;